import { defineStore } from 'pinia';
import { computed } from 'vue';

import { peanudsApi, withAuthenticatedHeaders } from '@peanuds/shared';
import type {
  GenerateKycLinkData,
  GenerateKycLinkBodyDto,
  GetKycStatusData,
  GetKycStatusResponseDtoOutput,
} from '@peanuds/shared';

import { createVerificationSource } from './createVerificationSource';

export const useKycSourceStore = defineStore('kyc-source', () => {
  const {
    source: liveKyc,
    isLoading: isKycLoading,
    reset: resetKycState,
    fetch: fetchLiveKycStatus,
    ensureResolved: ensureLiveKycResolved,
  } = createVerificationSource<GetKycStatusResponseDtoOutput>({
    sourceLabel: 'KYC',
    load: async (userId) => {
      const requestOptions = await withAuthenticatedHeaders<GetKycStatusData>({
        path: { id: userId },
      });

      const response = await peanudsApi.getKycStatus(requestOptions);

      return response.data ?? null;
    },
  });

  const kyc = computed(() => liveKyc.value);

  const fetchKycStatus = async (userId: string) => {
    await fetchLiveKycStatus(userId);
  };

  const ensureKycResolved = async (userId: string) => {
    await ensureLiveKycResolved(userId);
  };

  const generateKycLink = async (
    userId: string,
    kycScope: GenerateKycLinkBodyDto['kycScope'] = 'CORPORATE',
  ) => {
    if (!userId) {
      console.error('Missing user ID for KYC link generation');
      return null;
    }

    try {
      const response = await peanudsApi.generateKycLink(
        await withAuthenticatedHeaders<GenerateKycLinkData>({
          body: { kycScope },
          path: { id: userId },
        }),
      );

      return response.data?.url ?? null;
    } catch (error) {
      console.error('Failed to generate KYC link:', error);
      return null;
    }
  };

  return {
    kyc,
    isKycLoading,
    resetKycState,
    fetchKycStatus,
    ensureKycResolved,
    generateKycLink,
  };
});
